import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from './context-menu.model';
import { filter, map } from 'rxjs/operators';
import { APP_BASE_HREF } from '@angular/common';
import { PermissionRoutingService } from '../permission-routing.service';
import { Org, User } from '../../models/user';
import { OrgSiteSelectionService } from '../org-site-selection/org-site-selection.service';
import { Urls } from 'src/app/models/urls';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';

@Component({
    selector: 'app-context-menu',
    templateUrl: './context-menu.component.html',
    styleUrls: ['./context-menu.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ContextMenuComponent implements OnInit {
    private readonly menuItems: MenuItem[];

    private readonly url$: Observable<string>;

    Urls = Urls;

    menuItems$: Observable<MenuItem[]>;

    constructor(
        private readonly router: Router,
        @Inject(APP_BASE_HREF)
        public baseHref: string,
        private readonly orgSiteSelectionService: OrgSiteSelectionService
    ) {
        // the context menu is supposed to be shared among projects. Therefore, it can't assume
        // baseURL. It needs to always use absolute path or full URL.
        this.menuItems = [
            {
                href: Urls.MANAGE_USERS,
                labelText: 'Manage Users',
                newWindow: false
            },
            {
                href: Urls.DASHBOARD,
                labelText: 'Dashboard',
                newWindow: false
            }
        ];

        this.url$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).pipe(map(event => event.urlAfterRedirects));
    }

    @Input()
    user$: Observable<User>;

    @Output()
    logout = new EventEmitter();

    ngOnInit(): void {
        this.menuItems$ = combineLatest([of(this.menuItems), this.user$, this.url$, this.orgSiteSelectionService.getOrgSelected$()]).pipe(
            map(([menuItems, user, url, selectedOrg]) =>
                menuItems.filter(item => this.urlFilter(item, url)).filter(item => this.permissionFilter(item, user, selectedOrg))
            )
        );
    }

    urlFilter(item: MenuItem, url: string): boolean {
        return item.href === Urls.DASHBOARD ? !url.startsWith(Urls.DASHBOARD) : item.href !== url;
    }

    permissionFilter(item: MenuItem, user: User, selectedOrg: Org): boolean {
        const orgCode = selectedOrg.orgCode;
        if (!orgCode) {
            return false;
        }
        switch (item.href) {
            case Urls.MANAGE_USERS:
                // filter out if no org has userManagement feature
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.Admin, orgCode);
            case Urls.DASHBOARD:
                return (
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.GeneralAccess, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ClinicalFeedback, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestViewer, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestEditor, orgCode)
                );
        }

        return true;
    }

    doLogout(): void {
        this.logout.emit();
    }
}
